import { unwrap } from "@sagot/api-client";
import { CONSOLE_PATHS, RESEND_COOLDOWN_SECONDS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, cn } from "@sagot/ui";
import { useCallback, useEffect, useRef, useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../lib/api-errors";
import { useCountdown } from "../lib/use-countdown";
import { useApi } from "../session/session-provider";
import { FormAlert } from "./components/form-alert";
import { OtpInput, isCompleteOtp } from "./components/otp-input";
import { OTP_AUTO_SUBMIT_MS } from "./register-constants";
import { PaneTitle, RegisterNav, RegisterPane } from "./register-pane";

interface Note {
  tone: "ok" | "err";
  text: string;
}

interface RegisterVerifyPaneProps {
  email: string;
  back: boolean;
  /** "Change email" (the wizard returns to its first step; the verify page goes to the register page). */
  onChangeEmail: () => void;
  /** The API accepted the code; the body is the sign-in response (tokens), applied by the caller. */
  onVerified: (body: unknown) => void | Promise<void>;
}

/**
 * The code pane (mockup pane 2, "Check your email"): six boxes, automatic check after the sixth digit, resend with a countdown,
 * Change email. Wired to `POST /v1/auth/verify-email` and `/verify-email/resend`. Shared by the wizard and the `/verify-email` page.
 */
export function RegisterVerifyPane({ email, back, onChangeEmail, onVerified }: RegisterVerifyPaneProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const { remaining, restart } = useCountdown(RESEND_COOLDOWN_SECONDS);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [rejected, setRejected] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState<Note | null>(null);
  const complete = isCompleteOtp(code);

  const verify = useCallback(async () => {
    if (!isCompleteOtp(code) || verifying) return;
    setVerifying(true);
    setError(null);
    try {
      const body = await unwrap(api.POST("/v1/auth/verify-email", { body: { email, code } }));
      await onVerified(body);
    } catch (caught) {
      setError(message(caught));
      setRejected(code);
    } finally {
      setVerifying(false);
    }
  }, [api, code, email, message, onVerified, verifying]);

  const latest = useRef(verify);
  useEffect(() => {
    latest.current = verify;
  });

  // Mockup: the sixth digit starts the check by itself, once per code (a refused code waits for the visitor to change it).
  useEffect(() => {
    if (!complete || verifying || code === rejected) return undefined;
    const timer = window.setTimeout(() => {
      void latest.current();
    }, OTP_AUTO_SUBMIT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [code, complete, rejected, verifying]);

  const resend = async () => {
    setSending(true);
    setNote(null);
    try {
      await unwrap(api.POST("/v1/auth/verify-email/resend", { body: { email } }));
      setNote({ tone: "ok", text: t("auth.verify.resent", { email }) });
      restart();
    } catch (caught) {
      setNote({ tone: "err", text: message(caught) });
    } finally {
      setSending(false);
    }
  };

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    void verify();
  };

  return (
    <RegisterPane back={back} onSubmit={submit}>
      <PaneTitle>{t("auth.register.verifyTitle")}</PaneTitle>
      <p className="a-sub">
        {t("auth.register.verifySubLead")} <strong>{email}</strong>.
      </p>
      {error ? (
        <FormAlert
          title={error}
          onDismiss={() => {
            setError(null);
          }}
        />
      ) : null}
      <OtpInput id="r-otp-1" value={code} onChange={setCode} invalid={Boolean(error) && code === rejected} disabled={verifying} />
      <p className="resend">
        {t("auth.register.resendLead")}{" "}
        <button type="button" disabled={remaining > 0 || sending} onClick={() => void resend()}>
          {remaining > 0 ? t("auth.register.resendWait", { seconds: remaining }) : t("auth.register.resendNow")}
        </button>
      </p>
      <p className={cn("r-fine", note?.tone === "err" && "r-fine-err")} role="status">
        {note?.text}
      </p>
      <p className="r-fine">
        {t("auth.register.noCodeLead")}{" "}
        <AppLink href={CONSOLE_PATHS.login} className="a-link">
          {t("auth.register.noCodeSignIn")}
        </AppLink>{" "}
        {t("auth.register.noCodeOr")}{" "}
        <AppLink href={CONSOLE_PATHS.forgotPassword} className="a-link">
          {t("auth.register.noCodeReset")}
        </AppLink>
        .
      </p>
      <RegisterNav backLabel={t("auth.register.changeEmail")} onBack={onChangeEmail}>
        <Button type="submit" size="lg" loading={verifying} disabled={!complete || verifying}>
          {t("auth.register.verifyAction")}
        </Button>
      </RegisterNav>
    </RegisterPane>
  );
}
